import { diagnoseMapDocument } from './diagnose.ts';
import { migrateMapDocument } from './migrate.ts';
import { DEFAULT_MAP_HEIGHT, DEFAULT_MAP_WIDTH } from './types.ts';
import type { MapDocument } from './types.ts';
import { CURRENT_MAP_VERSION, OLDEST_SUPPORTED_MAP_VERSION } from './version.ts';

export type MapImportNoteCode = 'converted' | 'dropped-props';

export type MapImportNote = {
  code: MapImportNoteCode;
  message: string;
};

export type MapImportIssueCode =
  | 'not-json'
  | 'not-a-map'
  | 'no-version'
  | 'too-old'
  | 'too-new'
  | 'missing-bounds'
  | 'missing-islands';

export type MapImportIssue = {
  code: MapImportIssueCode;
  message: string;
};

export type MapImportFixId = 'stamp-version' | 'coerce-version' | 'add-islands' | 'add-bounds';

export type MapImportFix = {
  id: MapImportFixId;
  label: string;
};

export type MapImportResult =
  | { ok: true; map: MapDocument; notes: MapImportNote[] }
  | { ok: false; errors: MapImportIssue[]; fix: MapImportFix | null };

const VERSION_RANGE = `${OLDEST_SUPPORTED_MAP_VERSION}–${CURRENT_MAP_VERSION}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Whole-number text like `"5"`, not `"5.0"` or `"version 5"`. */
function isIntegerVersionString(value: unknown): value is string {
  return typeof value === 'string' && /^-?\d+$/.test(value.trim());
}

function parsedIntegerVersion(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (isIntegerVersionString(value)) {
    return Number(value.trim());
  }

  return null;
}

function isSupportedVersion(version: number): boolean {
  return version >= OLDEST_SUPPORTED_MAP_VERSION && version <= CURRENT_MAP_VERSION;
}

function hasBounds(value: Record<string, unknown>): boolean {
  return isRecord(value.bounds);
}

function hasIslands(value: Record<string, unknown>): boolean {
  return Array.isArray(value.islands);
}

function tooOldMessage(version: number): string {
  return `That map is too old (version ${version}). This build reads versions ${VERSION_RANGE}.`;
}

function tooNewMessage(version: number): string {
  return `That map is too new (version ${version}). This build reads up to version ${CURRENT_MAP_VERSION}.`;
}

function collectNotes(raw: unknown, map: MapDocument): MapImportNote[] {
  if (!isRecord(raw)) {
    return [];
  }

  const notes: MapImportNote[] = [];
  const sourceVersion = parsedIntegerVersion(raw.version);

  if (sourceVersion !== null && sourceVersion !== map.version) {
    notes.push({
      code: 'converted',
      message: `Imported and converted from version ${sourceVersion} to ${map.version}.`,
    });
  }

  if (Array.isArray(raw.props) && raw.props.length > 0) {
    notes.push({
      code: 'dropped-props',
      message: 'Stone and vegetation props were removed.',
    });
  }

  return notes;
}

function collectErrors(value: unknown): MapImportIssue[] {
  if (!isRecord(value)) {
    return [{ code: 'not-a-map', message: 'That file is not a Battleboats map.' }];
  }

  const errors: MapImportIssue[] = [];
  const version = value.version;
  const numeric = parsedIntegerVersion(version);

  if (typeof version !== 'number' || !Number.isFinite(version)) {
    if (numeric !== null && numeric < OLDEST_SUPPORTED_MAP_VERSION) {
      errors.push({ code: 'too-old', message: tooOldMessage(numeric) });
    } else if (numeric !== null && numeric > CURRENT_MAP_VERSION) {
      errors.push({ code: 'too-new', message: tooNewMessage(numeric) });
    } else {
      errors.push({ code: 'no-version', message: 'That file has no map version.' });
    }
  } else if (version < OLDEST_SUPPORTED_MAP_VERSION) {
    errors.push({ code: 'too-old', message: tooOldMessage(version) });
  } else if (version > CURRENT_MAP_VERSION) {
    errors.push({ code: 'too-new', message: tooNewMessage(version) });
  }

  if (!hasBounds(value)) {
    errors.push({ code: 'missing-bounds', message: 'That file is missing map bounds.' });
  }

  if (!hasIslands(value)) {
    errors.push({ code: 'missing-islands', message: 'That file is missing an islands list.' });
  }

  if (errors.length === 0) {
    const reason = diagnoseMapDocument(value);
    return [{ code: 'not-a-map', message: reason ?? 'That file is not a Battleboats map.' }];
  }

  return errors;
}

/**
 * One-field patches only. Two missing fields, an unsupported version, or random JSON get no fix.
 */
function suggestFix(value: unknown): MapImportFix | null {
  if (!isRecord(value)) {
    return null;
  }

  const boundsOk = hasBounds(value);
  const islandsOk = hasIslands(value);
  const numeric = parsedIntegerVersion(value.version);
  const finiteNumber = typeof value.version === 'number' && Number.isFinite(value.version);

  if (isIntegerVersionString(value.version) && boundsOk && islandsOk) {
    if (numeric !== null && isSupportedVersion(numeric)) {
      return { id: 'coerce-version', label: `Use version ${numeric}` };
    }

    return null;
  }

  if (!finiteNumber && boundsOk && islandsOk) {
    return { id: 'stamp-version', label: `Convert to version ${CURRENT_MAP_VERSION}` };
  }

  if (finiteNumber && numeric !== null && isSupportedVersion(numeric) && boundsOk && !islandsOk) {
    return { id: 'add-islands', label: 'Add empty islands' };
  }

  if (finiteNumber && numeric !== null && isSupportedVersion(numeric) && islandsOk && !boundsOk) {
    return { id: 'add-bounds', label: 'Add default bounds' };
  }

  return null;
}

function applyFix(value: Record<string, unknown>, fixId: MapImportFixId): Record<string, unknown> {
  switch (fixId) {
    case 'stamp-version':
      return { ...value, version: CURRENT_MAP_VERSION };
    case 'coerce-version': {
      const version = parsedIntegerVersion(value.version);
      return version === null ? value : { ...value, version };
    }
    case 'add-islands':
      return { ...value, islands: [] };
    case 'add-bounds':
      return {
        ...value,
        bounds: { width: DEFAULT_MAP_WIDTH, height: DEFAULT_MAP_HEIGHT },
      };
  }
}

function importParsed(value: unknown): MapImportResult {
  const map = migrateMapDocument(value);

  if (map) {
    return { ok: true, map, notes: collectNotes(value, map) };
  }

  return { ok: false, errors: collectErrors(value), fix: suggestFix(value) };
}

/** Load a map file: migrate when we can, otherwise a specific reason and at most one simple fix. */
export function importMapDocument(json: string): MapImportResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch {
    return {
      ok: false,
      errors: [{ code: 'not-json', message: 'That file is not valid JSON.' }],
      fix: null,
    };
  }

  return importParsed(parsed);
}

/** Re-run import after applying the one suggested field patch. */
export function applyMapImportFix(json: string, fixId: MapImportFixId): MapImportResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch {
    return {
      ok: false,
      errors: [{ code: 'not-json', message: 'That file is not valid JSON.' }],
      fix: null,
    };
  }

  const suggested = suggestFix(parsed);

  if (!isRecord(parsed) || suggested?.id !== fixId) {
    return importParsed(parsed);
  }

  return importParsed(applyFix(parsed, fixId));
}

export function importFailureMessage(result: Extract<MapImportResult, { ok: false }>): string {
  return result.errors.map((issue) => issue.message).join(' ');
}

export function importSuccessMessage(notes: readonly MapImportNote[]): string {
  const converted = notes.find((note) => note.code === 'converted');
  return converted?.message ?? 'Map imported.';
}
